import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { toast } from "sonner";

type FormData = {
  sourceTeamId: string;
  targetTeamId: string;
  overlayTemplateId: string;
};

type TeamInfo = {
  name: string;
  loading: boolean;
  error: string;
};

type TemplateInfo = {
  name: string;
  loading: boolean;
  error: string;
};

type CopyResult = {
  newTemplateId: number;
  copiedAssetMediaCount: number;
} | null;

export default function CopyOverlayTemplate() {
  const [formData, setFormData] = useState<FormData>({
    sourceTeamId: "",
    targetTeamId: "",
    overlayTemplateId: "",
  });

  const [sourceTeamInfo, setSourceTeamInfo] = useState<TeamInfo>({
    name: "",
    loading: false,
    error: "",
  });

  const [targetTeamInfo, setTargetTeamInfo] = useState<TeamInfo>({
    name: "",
    loading: false,
    error: "",
  });

  const [templateInfo, setTemplateInfo] = useState<TemplateInfo>({
    name: "",
    loading: false,
    error: "",
  });

  const [copyResult, setCopyResult] = useState<CopyResult>(null);

  // Helper function for form updates
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Function to fetch team name by ID
  const fetchTeamName = async (
    teamId: string,
    setTeamInfo: React.Dispatch<React.SetStateAction<TeamInfo>>,
  ) => {
    const parsedTeamId = parseInt(teamId);
    if (isNaN(parsedTeamId) || parsedTeamId <= 0) {
      setTeamInfo({ name: "", loading: false, error: "" });
      return;
    }

    setTeamInfo({ name: "", loading: true, error: "" });

    try {
      const team = await AdminApi.getTeamById(parsedTeamId);
      setTeamInfo({ name: team.name, loading: false, error: "" });
    } catch (error) {
      console.error("Failed to fetch team:", error);
      setTeamInfo({ name: "", loading: false, error: "Team not found" });
    }
  };

  // Function to fetch template name by ID
  const fetchTemplateName = async (templateId: string) => {
    const parsedTemplateId = parseInt(templateId);
    if (isNaN(parsedTemplateId) || parsedTemplateId <= 0) {
      setTemplateInfo({ name: "", loading: false, error: "" });
      return;
    }

    setTemplateInfo({ name: "", loading: true, error: "" });

    try {
      const template = await AdminApi.getOverlayTemplateById(parsedTemplateId);
      setTemplateInfo({ name: template.name, loading: false, error: "" });
    } catch (error) {
      console.error("Failed to fetch template:", error);
      setTemplateInfo({
        name: "",
        loading: false,
        error: "Template not found",
      });
    }
  };

  // Handle team ID blur events
  const handleSourceTeamIdBlur = () => {
    if (formData.sourceTeamId.trim()) {
      fetchTeamName(formData.sourceTeamId.trim(), setSourceTeamInfo);
    } else {
      setSourceTeamInfo({ name: "", loading: false, error: "" });
    }
  };

  const handleTargetTeamIdBlur = () => {
    if (formData.targetTeamId.trim()) {
      fetchTeamName(formData.targetTeamId.trim(), setTargetTeamInfo);
    } else {
      setTargetTeamInfo({ name: "", loading: false, error: "" });
    }
  };

  const handleTemplateIdBlur = () => {
    if (formData.overlayTemplateId.trim()) {
      fetchTemplateName(formData.overlayTemplateId.trim());
    } else {
      setTemplateInfo({ name: "", loading: false, error: "" });
    }
  };

  // Mutation for copying overlay template
  const copyMutation = useMutation({
    mutationFn: AdminApi.copyOverlayTemplate,
    onSuccess: (result) => {
      toast.success(
        `Successfully copied overlay template! New template ID: ${result.newTemplateId}`,
      );
      // Set result to display in UI
      setCopyResult({
        newTemplateId: result.newTemplateId,
        copiedAssetMediaCount: result.copiedAssetMediaCount,
      });
      // Reset form
      setFormData({
        sourceTeamId: "",
        targetTeamId: "",
        overlayTemplateId: "",
      });
      setSourceTeamInfo({ name: "", loading: false, error: "" });
      setTargetTeamInfo({ name: "", loading: false, error: "" });
      setTemplateInfo({ name: "", loading: false, error: "" });
    },
    onError: (error) => {
      console.error("Failed to copy overlay template:", error);
      toast.error(`Failed to copy overlay template: ${error.message}`);
      setCopyResult(null);
    },
  });

  // Validation function
  const validateForm = (): string | null => {
    const parsedSourceTeamId = parseInt(formData.sourceTeamId);
    if (isNaN(parsedSourceTeamId) || parsedSourceTeamId <= 0) {
      return "Please enter a valid source team ID";
    }

    const parsedTargetTeamId = parseInt(formData.targetTeamId);
    if (isNaN(parsedTargetTeamId) || parsedTargetTeamId <= 0) {
      return "Please enter a valid target team ID";
    }

    const parsedTemplateId = parseInt(formData.overlayTemplateId);
    if (isNaN(parsedTemplateId) || parsedTemplateId <= 0) {
      return "Please enter a valid overlay template ID";
    }

    if (parsedSourceTeamId === parsedTargetTeamId) {
      return "Source and target teams must be different";
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    copyMutation.mutate({
      sourceTeamId: parseInt(formData.sourceTeamId),
      targetTeamId: parseInt(formData.targetTeamId),
      overlayTemplateId: parseInt(formData.overlayTemplateId),
    });
  };

  return (
    <AdminLayout
      title="Copy Overlay Template"
      description="Copy an overlay template from one team to another, including all media assets"
      breadcrumb="Copy Overlay Template"
    >
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This will copy the overlay template and all
              associated media assets (images/videos) from the source team to
              the target team. New files will be created in S3 and new database
              records will be created for the target team.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Source Team ID */}
            <div>
              <label
                htmlFor="sourceTeamId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Source Team ID
              </label>
              <input
                type="number"
                id="sourceTeamId"
                value={formData.sourceTeamId}
                onChange={(e) => {
                  updateFormData({ sourceTeamId: e.target.value });
                  // Clear team info when typing
                  if (sourceTeamInfo.name || sourceTeamInfo.error) {
                    setSourceTeamInfo({ name: "", loading: false, error: "" });
                  }
                }}
                onBlur={handleSourceTeamIdBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123"
                min="1"
                required
              />
              {/* Source team name display */}
              {formData.sourceTeamId && (
                <div className="mt-2">
                  {sourceTeamInfo.loading && (
                    <div className="text-sm text-gray-500">
                      Loading team name...
                    </div>
                  )}
                  {sourceTeamInfo.name && (
                    <div className="text-sm text-green-700">
                      Team: {sourceTeamInfo.name}
                    </div>
                  )}
                  {sourceTeamInfo.error && (
                    <div className="text-sm text-red-600">
                      {sourceTeamInfo.error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Target Team ID */}
            <div>
              <label
                htmlFor="targetTeamId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Target Team ID
              </label>
              <input
                type="number"
                id="targetTeamId"
                value={formData.targetTeamId}
                onChange={(e) => {
                  updateFormData({ targetTeamId: e.target.value });
                  // Clear team info when typing
                  if (targetTeamInfo.name || targetTeamInfo.error) {
                    setTargetTeamInfo({ name: "", loading: false, error: "" });
                  }
                }}
                onBlur={handleTargetTeamIdBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="456"
                min="1"
                required
              />
              {/* Target team name display */}
              {formData.targetTeamId && (
                <div className="mt-2">
                  {targetTeamInfo.loading && (
                    <div className="text-sm text-gray-500">
                      Loading team name...
                    </div>
                  )}
                  {targetTeamInfo.name && (
                    <div className="text-sm text-green-700">
                      Team: {targetTeamInfo.name}
                    </div>
                  )}
                  {targetTeamInfo.error && (
                    <div className="text-sm text-red-600">
                      {targetTeamInfo.error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Overlay Template ID */}
            <div>
              <label
                htmlFor="overlayTemplateId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Overlay Template ID
              </label>
              <input
                type="number"
                id="overlayTemplateId"
                value={formData.overlayTemplateId}
                onChange={(e) => {
                  updateFormData({ overlayTemplateId: e.target.value });
                  // Clear template info when typing
                  if (templateInfo.name || templateInfo.error) {
                    setTemplateInfo({ name: "", loading: false, error: "" });
                  }
                }}
                onBlur={handleTemplateIdBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="789"
                min="1"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                The internal database ID of the overlay template to copy from
                the source team
              </p>
              {/* Template name display */}
              {formData.overlayTemplateId && (
                <div className="mt-2">
                  {templateInfo.loading && (
                    <div className="text-sm text-gray-500">
                      Loading template name...
                    </div>
                  )}
                  {templateInfo.name && (
                    <div className="text-sm text-green-700">
                      Template: {templateInfo.name}
                    </div>
                  )}
                  {templateInfo.error && (
                    <div className="text-sm text-red-600">
                      {templateInfo.error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={copyMutation.isPending}
                className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {copyMutation.isPending
                  ? "Copying Template..."
                  : "Copy Overlay Template"}
              </button>
            </div>
          </form>
        </div>

        {/* Result Display */}
        {copyResult && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ✅ Copy Successful
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-600">
                  New Template ID:
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {copyResult.newTemplateId}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-600">
                  Media Assets Copied:
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {copyResult.copiedAssetMediaCount}
                </span>
              </div>
            </div>
            <button
              onClick={() => setCopyResult(null)}
              className="mt-4 w-full py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Result
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
